// Sidebar structural primitives — header, footer, and group
// containers. Pure presentational forwardRefs; no internal state.

import * as React from 'react'
import { Slot } from '@/components/ui/slot'
import { cn } from '../../../../src/cn'

const SIDEBAR_GROUP_LABEL_CLASS = 'duration-200 flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-sidebar-foreground/70 outline-none ring-sidebar-ring transition-all ease-linear focus-visible:ring-2 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:sm:h-4 [&>svg]:sm:w-4 [&>svg]:md:h-5 [&>svg]:md:w-5 [&>svg]:lg:h-5 [&>svg]:lg:w-5 [&>svg]:xl:h-6 [&>svg]:xl:w-6 [&>svg]:shrink-0 [&>svg]:transition-all [&>svg]:duration-200'

const SIDEBAR_GROUP_ACTION_CLASS = 'absolute right-3 top-3.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-none ring-sidebar-ring transition-transform hover:bg-sidebar-accent-soft hover:text-sidebar-accent-soft-foreground focus-visible:ring-2 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:sm:h-4 [&>svg]:sm:w-4 [&>svg]:md:h-5 [&>svg]:md:w-5 [&>svg]:lg:h-5 [&>svg]:lg:w-5 [&>svg]:xl:h-6 [&>svg]:xl:w-6 [&>svg]:shrink-0 [&>svg]:transition-all [&>svg]:duration-200'

export const SidebarHeader = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-2 p-4 sm:p-6 min-w-0', className)} {...props} />
  ),
)
SidebarHeader.displayName = 'SidebarHeader'

export const SidebarFooter = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-2 p-4 sm:p-6 min-w-0', className)} {...props} />
  ),
)
SidebarFooter.displayName = 'SidebarFooter'

export const SidebarGroup = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('relative flex w-full min-w-0 flex-col px-3 py-4 sm:py-6', className)}
      {...props}
    />
  ),
)
SidebarGroup.displayName = 'SidebarGroup'

export const SidebarGroupLabel = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & { asChild?: boolean }
>(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'div'
  return <Comp ref={ref} className={cn(SIDEBAR_GROUP_LABEL_CLASS, className)} {...props} />
})
SidebarGroupLabel.displayName = 'SidebarGroupLabel'

export const SidebarGroupAction = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> & { asChild?: boolean }
>(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button'
  return <Comp ref={ref} className={cn(SIDEBAR_GROUP_ACTION_CLASS, className)} {...props} />
})
SidebarGroupAction.displayName = 'SidebarGroupAction'

export const SidebarGroupContent = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('w-full text-sm', className)} {...props} />
  ),
)
SidebarGroupContent.displayName = 'SidebarGroupContent'
